import { useEffect, useRef, useMemo } from "react";
import { Form, Input, Row, Col, Select, Switch, message, Tooltip } from "antd";
import MultiSelector from "./MultiSelector";
import { InfoCircleOutlined } from "@ant-design/icons";
import { validateElementsAndPeriodicities } from "../utils/validators";

const ElementsDeviceInfoTab = ({
  form,
  mode,
  isVirtual,
  availableElements,
  periodicities,
  loadingDeviceElements,
  loadingElements,
  disableElementsTab,
}) => {
  
  const watchedSelectedElements = Form.useWatch("selectedElements", form) || [];
  const watchedElementPeriodicities = Form.useWatch("elementPeriodicities", form) || {};

  const prevSelectedElementsRef = useRef(watchedSelectedElements);
  const prevElementPeriodicitiesRef = useRef(watchedElementPeriodicities);

  const hasThresholdFor = (elementId, periodicityId) => {
    const thresholdsDraft = form.getFieldValue("thresholdsDraft") || [];
    if (periodicityId == null) {
      return thresholdsDraft.some((t) => t.elementId === elementId);
    }
    return thresholdsDraft.some(
      (t) => t.elementId === elementId && t.periodicityId === periodicityId
    );
  };

  const elementOrderMap = useMemo(() => {
    const map = {};
    availableElements.forEach((el, index) => {
      map[el.id] = index;
    });
    return map;
  }, [availableElements]);

  useEffect(() => {
    const currentEP = form.getFieldValue("elementPeriodicities") || {};
    const cleaned = {};

    watchedSelectedElements.forEach((id) => {
      if (currentEP[id]) {
        cleaned[id] = currentEP[id];
      }
    });

    if (Object.keys(cleaned).length !== Object.keys(currentEP).length) {
      form.setFieldsValue({ elementPeriodicities: cleaned });
    }
  }, [watchedSelectedElements, form]);

  return (
    <>
      <div
        style={{
          padding: 10,
          height: 600,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {mode === "edit" && (loadingDeviceElements || loadingElements) ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <span>Loading elements...</span>
          </div>
        ) : (
          <>
            {/* <Form.Item name="selectedElements" hidden initialValue={[]}>
              <Input />
            </Form.Item> */}

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="selectedElements"
                  // label="Elements and aggregation"
                  required
                  // tooltip="Select one or more aggregation for each element"
                  label={
                    <span>
                      Elements and Aggregations
                      <Tooltip 
                        title="Select one or more aggregation for each element"
                        getPopupContainer={(trigger) =>
                          trigger.closest(".ant-modal")
                        }
                      >
                        <InfoCircleOutlined style={{ marginLeft: 6 }} />
                      </Tooltip>
                    </span>
                  }
                  style={{ marginBottom: 8 }}
                  validateTrigger="onSubmit"
                  rules={[
                    {
                      validator: validateElementsAndPeriodicities(form),
                    },
                  ]}
                >
                  <MultiSelector
                    disabled={disableElementsTab}
                    items={availableElements.map((e) => ({
                      id: e.id,
                      name: `${e.name} (${e.unit})`,
                    }))}
                    onChange={(nextSelected) => {
                      if (disableElementsTab) return;

                      const prevSelected = prevSelectedElementsRef.current || [];
                      const removed = prevSelected.filter((id) => !nextSelected.includes(id));

                      // Si intenta quitar un elemento que tiene thresholds -> bloquear y revertir
                      const blocked = removed.find((elementId) => hasThresholdFor(elementId));
                      if (blocked != null) {
                        message.warning(
                          "You cannot remove this element because it has thresholds configured. Remove thresholds first."
                        );
                        // revertir
                        form.setFieldsValue({ selectedElements: prevSelected });
                        return;
                      }

                      form.setFieldsValue({ selectedElements: nextSelected });
                      prevSelectedElementsRef.current = nextSelected;
                      form.validateFields(["selectedElements"]).catch(() => {});
                    }}
                    width="100%"
                  />
                </Form.Item>
              </Col>

              {mode === "edit" && !isVirtual && (
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="hasAnemometer"
                    label="Has Anemometer"
                    valuePropName="checked"
                  >
                    <Switch disabled={disableElementsTab} />
                  </Form.Item>
                </Col>
              )}
            </Row>

            <div
              style={{
                flex: 1,
                overflowY: "auto",
                paddingRight: 8,
                marginTop: 8,
              }}
            >
              {[...new Set(watchedSelectedElements || [])]
                .sort(
                  (a, b) =>
                    (elementOrderMap[a] ?? Number.MAX_SAFE_INTEGER) -
                    (elementOrderMap[b] ?? Number.MAX_SAFE_INTEGER)
                )
                .map((id) => {
                  const element = availableElements.find((e) => e.id === id);
                  if (!element) return null;

                  return (
                    <div
                      key={id}
                      style={{
                        border: "1px solid #eee",
                        padding: "10px 12px",
                        borderRadius: 8,
                        marginBottom: 10,
                        background: "#fafafa",
                      }}
                    >
                      <Row align="middle" gutter={8}>
                        <Col flex="auto">
                          <strong>{element.name}</strong> ({element.unit})
                        </Col>
                        <Col flex="400px">
                          <Form.Item
                            name={["elementPeriodicities", String(id)]}
                            style={{ margin: 0 }}
                            rules={[
                              {
                                required: true,
                                message: "Select at least one aggregation",
                              },
                            ]}
                          >
                            <Select
                              disabled={disableElementsTab}
                              mode="multiple"
                              placeholder="Select aggregation"
                              options={periodicities.map((p) => ({
                                label: p.name,
                                value: p.id,
                              }))}
                              getPopupContainer={(trigger) => trigger.parentNode}
                              style={{
                                width: "100%",
                              }}
                              onChange={(nextPeriodicityIds) => {
                                if (disableElementsTab) return;

                                const prevEP = prevElementPeriodicitiesRef.current || {};
                                const prevList = prevEP[id] || [];

                                const removed = prevList.filter((pId) => !nextPeriodicityIds.includes(pId));

                                // Si intenta quitar periodicidad con thresholds -> bloquear y revertir
                                const blocked = removed.find((pId) => hasThresholdFor(id, pId));
                                if (blocked != null) {
                                  message.warning(
                                    "You cannot remove this aggregation because it has thresholds configured. Remove thresholds first."
                                  );
                                  form.setFieldsValue({
                                    elementPeriodicities: {
                                      ...(watchedElementPeriodicities || {}),
                                      [id]: prevList,
                                    },
                                  });
                                  return;
                                }

                                // ok, dejamos el cambio y validamos
                                prevElementPeriodicitiesRef.current = {
                                  ...(watchedElementPeriodicities || {}),
                                  [id]: nextPeriodicityIds,
                                };

                                form.validateFields(["selectedElements"]).catch(() => {});
                              }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ElementsDeviceInfoTab;
